import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Button,
  Typography,
  Input,
  Select,
  Option,
  Dialog,
  DialogBody,
  DialogFooter,
  Alert,
} from '@material-tailwind/react'
import { PiUserPlusLight } from 'react-icons/pi'
import { useTranslation } from 'react-i18next'
import ShortUniqueId from 'short-unique-id'
import RoleChip from '../components/RoleChip'
import { validateEmail } from '../utils/helpers/validate.helpers'
import { inviteUserWithCode } from '../utils/helpers/supabase.helpers'
import { getRoleId } from '../utils/helpers/roles.helpers'
import { useGeneralToast } from '../hooks/toast'
import Container from '../components/Container'
import GoBack from '../components/GoBack'
import { useDarkMode } from '../contexts/DarkModeContext'

const { randomUUID } = new ShortUniqueId({ length: 10 })

const InviteNewUser = () => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const { success: toastSuccess, error: toastError } = useGeneralToast()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('')
  const [emailErr, setEmailErr] = useState(false)
  const [roleErr, setRoleErr] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const onEmailChange = ({ target }: { target: { value: string } }) => {
    setEmailErr(false)
    setEmail(target.value)
  }

  const onRoleChange = (value: string | undefined) => {
    setRoleErr(false)
    setRole(value || '')
  }

  const onSubmit = () => {
    if (!validateEmail(email)) {
      setEmailErr(true)
    }

    if (!role) {
      setRoleErr(true)
    }

    if (validateEmail(email) && role) {
      setShowConfirm(true)
    }
  }

  const onConfirm = async () => {
    setShowConfirm(false)

    setIsLoading(true)

    const code = randomUUID()

    const roleId = getRoleId(role)

    const { error } = await inviteUserWithCode({ email, role: roleId, code })

    if (error) {
      toastError((error as { message?: string })?.message ?? JSON.stringify(error))
    } else {
      toastSuccess(t('adminInviteUser.success'))
    }

    setIsLoading(false)
  }

  return (
    <Container direction="col" className="text-primary">
      <Dialog open={showConfirm} size="xs" handler={() => setShowConfirm(false)}>
        <DialogBody className="text-black">
          {t('adminInviteUser.confirmDialog.title')} <strong>{email}</strong>{' '}
          {t('adminInviteUser.confirmDialog.forRole')}{' '}
          <span className="inline-block">
            <RoleChip role={getRoleId(role)} />
          </span>
          ?
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button size="sm" color="red" variant="outlined" onClick={() => setShowConfirm(false)}>
            {t('adminInviteUser.confirmDialog.cancel')}
          </Button>
          <Button size="sm" color="yellow" onClick={onConfirm}>
            {t('adminInviteUser.confirmDialog.confirm')}
          </Button>
        </DialogFooter>
      </Dialog>

      <div className="mb-2 self-start">
        <GoBack />
      </div>

      <div className="flex flex-col max-w-lg mx-auto mb-12">
        <Typography variant="lead">{t('adminInviteUser.title')}</Typography>

        <div className="my-6">
          <Alert color="blue" variant="ghost" icon={<PiUserPlusLight className="h-8 w-8" />}>
            {t('adminInviteUser.description')}{' '}
            <Link to="/admin/new-code" className="underline">
              {t('adminInviteUser.clickHere')}
            </Link>{' '}
            {t('adminInviteUser.toGenerateCode')}
          </Alert>
        </div>

        <div className="my-2">
          <Input
            type="email"
            label={t('adminInviteUser.emailLabel')}
            value={email}
            onChange={onEmailChange}
            error={emailErr}
          />

          <Typography style={{ visibility: emailErr ? 'visible' : 'hidden' }} variant="small" color="red">
            {t('adminInviteUser.insertValidEmail')}
          </Typography>
        </div>

        <div className="my-2">
          <Select
            label={t('adminInviteUser.planLabel')}
            error={roleErr}
            value={role}
            onChange={onRoleChange}
            menuProps={{ className: 'dark:bg-primary' }}
            {...(darkMode && { color: 'blue-gray' })}
            labelProps={{
              className:
                'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
            }}
          >
            <Option value="free">
              <RoleChip role={getRoleId('free')} />
            </Option>
            <Option value="early_bird">
              <RoleChip role={getRoleId('early_bird')} />
            </Option>
            <Option value="content_creator">
              <RoleChip role={getRoleId('content_creator')} />
            </Option>
            <Option value="venue">
              <RoleChip role={getRoleId('venue')} />
            </Option>
          </Select>

          <Typography style={{ visibility: roleErr ? 'visible' : 'hidden' }} variant="small" color="red">
            {t('adminInviteUser.choosePlan')}
          </Typography>
        </div>

        <div className="my-2">
          <Button color="yellow" size="sm" onClick={onSubmit} disabled={isLoading}>
            {t('adminInviteUser.sendInvite')}
          </Button>
        </div>
      </div>
    </Container>
  )
}

export default InviteNewUser
